import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClaimTabProps } from '../claim-registration';
import { useTranslation } from 'react-i18next';
import LossDetails from './loss-details';
import { Button, Form, message } from 'antd';
import ClaimBinderDetails from './claim-binder-details';
import ProgramRIConsiderations from './program-ri-considerations';
import Comments from './comments';
import BinderClaimsFinanacialSummary from './binder-claims-financial-summary';
import { useNavigate, useSearchParams } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import {
  binderClaimApi,
  catEventApi,
  claimMasterReserveApi,
} from 'services/apis';
import dayjs from 'dayjs';
import { UpdateBinderClaimPayloadDto } from 'libs/pinnacle-sdk';
import { handleError, validateForm } from 'utils/form';
import { fields, getValidationSchema } from './form';

const BinderClaimsActions: React.FC<ClaimTabProps> = ({
  claim,
  includeNext,
  includeBack,
  navigateNext,
  navigateBack,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimNo = searchParams.get('claimNo');

  const { data: binderClaim } = useQuery(
    [EQueryKeys.GET_BINDER_CLAIM_BY_ID, claimNo],
    async () => {
      return binderClaimApi.getBinderClaimById({
        claimNo: claimNo as string,
        includeInsured: true,
        includeReinsured: true,
        includeBroker: true,
        includeClaimHandlerUser: true,
      });
    },
    {
      enabled: !!claimNo,
    }
  );

  // Fetch cat event details to get the event name
  const catEventId = binderClaim?.claim?.catCode;
  const { data: catEvent } = useQuery(
    [EQueryKeys.CAT_EVENT_BY_ID, catEventId],
    async () => {
      return catEventApi.getCatEventById({
        catEventId: catEventId as string,
      });
    },
    {
      enabled: !!catEventId,
    }
  );

  const claimId = binderClaim?.claimId;
  const contractId = binderClaim?.contractId;

  const { data: masterReserve } = useQuery(
    [EQueryKeys.LIST_MASTER_RESERVE_BY_CLAIM_ID, claimId],
    async () => {
      const masterReserveList =
        await claimMasterReserveApi.getMasterReserveListByClaimId({
          claimId: claimId as string,
          page: 1,
          pageSize: 1,
        });
      return masterReserveList?.items?.[0];
    },
    {
      enabled: !!claimId,
    }
  );

  const { data: financialSummaryData } = useQuery(
    [EQueryKeys.BINDER_CLAIM_FINANCIAL_SUMMARY, claimId, contractId],
    async () => {
      return binderClaimApi.getBinderClaimFinancialSummary({
        claimId: claimId as string,
        contractId: contractId as string,
      });
    },
    {
      enabled: !!claimId && !!contractId,
    }
  );

  // Prepare initial options for select fields
  const claimsHandlerInitialOption = useMemo(() => {
    const claimsHandlerUser = binderClaim?.claimsHandlerUser;
    const claimsHandlerId = binderClaim?.claim?.claimsHandler;
    if (claimsHandlerUser?.name && claimsHandlerId) {
      return {
        label: claimsHandlerUser.name,
        value: claimsHandlerId,
      };
    }
    return undefined;
  }, [binderClaim]);

  const catEventInitialOption = useMemo(() => {
    if (catEvent?.eventName && catEventId) {
      return {
        label: catEvent.eventName,
        value: catEventId,
      };
    }
    return undefined;
  }, [catEvent, catEventId]);

  useEffect(() => {
    const initializeForm = async () => {
      if (binderClaim) {
        const claimData = binderClaim.claim;
        const contractData = binderClaim.contract;

        const initialData = {
          claimsNo: binderClaim.claimNo,
          claimsHandler: claimData?.claimsHandler,
          claimsStatus: binderClaim.claimStatus,
          lineOfBusiness: claimData?.lineOfBusiness,
          subClass: claimData?.subClass,
          reinsured:
            contractData?.reinsured?.name || binderClaim.reinsured?.name,
          broker: contractData?.broker?.name,
          referenceNo: binderClaim.referenceNo,
          reportedDate: claimData?.dateReported
            ? dayjs(claimData.dateReported)
            : undefined,
          dateOfLoss: claimData?.dateOfLoss
            ? dayjs(claimData.dateOfLoss)
            : undefined,
          catCode: claimData?.catCode,
          causeOfLoss: claimData?.causeOfLoss,
          catEvent: claimData?.catCode,
          lossLocation: claimData?.lossLocation,
          city: claimData?.city,
          country: claimData?.country,
        };

        form.setFieldsValue(initialData);

        const isValid = await validateForm(
          initialData,
          form,
          fields as unknown as (keyof typeof initialData)[],
          getValidationSchema(t)
        );
        setIsFormValid(isValid);
      }
    };

    initializeForm();
  }, [binderClaim, form, t]);

  useEffect(() => {
    if (masterReserve) {
      form.setFieldsValue({
        programCessionDeclarationName:
          masterReserve.programCessionDeclarationName,
        programCession: masterReserve.programCessionPercent
          ? Number(masterReserve.programCessionPercent)
          : undefined,
        captiveSelfInsuredRetention: masterReserve.captiveSirsAag
          ? Number(masterReserve.captiveSirsAag)
          : undefined,
        riSelfInsuredRetention: masterReserve.riSirAag
          ? Number(masterReserve.riSirAag)
          : undefined,
        riCession: masterReserve.riCessionPercent
          ? Number(masterReserve.riCessionPercent)
          : undefined,
        riCosts: masterReserve.totalIncurredCosts
          ? Number(masterReserve.totalIncurredCosts)
          : undefined,
      });
    }
  }, [masterReserve, form]);

  const queryClient = useQueryClient();

  const updateBinderClaimMutation = useMutation(
    async (payload: UpdateBinderClaimPayloadDto) => {
      return binderClaimApi.updateBinderClaim({
        claimId: claimId as string,
        contractId: contractId as string,
        updateBinderClaimPayloadDto: payload,
      });
    },
    {
      onSuccess: () => {
        message.success(
          t('forms.updateRecordSuccess', { type: t('claim.binderClaim') })
        );
        queryClient.invalidateQueries([EQueryKeys.GET_BINDER_CLAIM_BY_ID]);
        queryClient.invalidateQueries([
          EQueryKeys.BINDER_CLAIM_FINANCIAL_SUMMARY,
        ]);
        navigate(
          ENavigationPath.CLAIM_DETAIL.replace(
            ':claimId',
            claim.claimId
          ).replace(':tabId', '3')
        );
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('forms.updateRecordFail', {
            type: t('claim.binderClaim'),
          }),
        });
      },
    }
  );

  const handleClose = useCallback(() => {
    navigate(
      ENavigationPath.CLAIM_DETAIL.replace(':claimId', claim.claimId).replace(
        ':tabId',
        '3'
      )
    );
  }, [claim.claimId, navigate]);

  const handleSave = useCallback(() => {
    form.validateFields().then((values) => {
      const payload: UpdateBinderClaimPayloadDto = {
        claimsHandler: values.claimsHandler,
        claimStatus: values.claimsStatus,
        referenceNo: values.referenceNo,
        dateReported: values.reportedDate?.toDate(),
        dateOfLoss: values.dateOfLoss?.toDate(),
        catCode: values.catCode,
        causeOfLoss: values.causeOfLoss,
        lossLocation: values.lossLocation,
        city: values.city,
        country: values.country,
        commentsTo: values.commentTo,
        comments: values.comment,
      };

      updateBinderClaimMutation.mutate(payload);
    });
  }, [form, updateBinderClaimMutation]);

  const handlePaymentReserveChange = useCallback(() => {
    navigate(
      ENavigationPath.PAYMENT_RESERVE_CHANGE.replace(
        ':claimId',
        claim.claimId
      ) + `?contractId=${contractId}&claimNo=${claimNo}`
    );
  }, [navigate, claim.claimId, contractId, claimNo]);

  return (
    <Form
      form={form}
      layout="vertical"
      className="space-y-8"
      initialValues={{}}
      onValuesChange={async () => {
        const values = form.getFieldsValue();
        const isValid = await validateForm(
          values,
          form,
          fields as unknown as (keyof typeof values)[],
          getValidationSchema(t)
        );
        setIsFormValid(isValid);
      }}
    >
      <LossDetails
        claimsHandlerInitialOption={claimsHandlerInitialOption}
        catEventInitialOption={catEventInitialOption}
      />

      <ClaimBinderDetails masterReserve={masterReserve} />

      <ProgramRIConsiderations />

      <BinderClaimsFinanacialSummary
        financialSummaryData={financialSummaryData}
      />

      <Comments />

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          className="w-full rounded-[12px] h-12 text-base font-medium"
          size="large"
          type="primary"
          variant="outlined"
          color="danger"
          onClick={handleClose}
        >
          {t('general.close')}
        </Button>
        <Button
          className="bg-[#1d67b6] w-full rounded-[12px] h-12 text-base font-medium"
          size="large"
          type="primary"
          onClick={handleSave}
          loading={updateBinderClaimMutation.isLoading}
          disabled={!isFormValid || updateBinderClaimMutation.isLoading}
        >
          {t('general.save')}
        </Button>
        <Button
          className="bg-[#1d67b6] w-full rounded-[12px] h-12 text-base font-medium"
          size="large"
          type="primary"
          onClick={handlePaymentReserveChange}
        >
          {t('general.paymentReserveChange')}
        </Button>
      </div>
    </Form>
  );
};

export default BinderClaimsActions;
